import { type ReactNode, useState } from 'react'
import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  type Placement,
} from '@floating-ui/react'
import { Portal } from '@/components/Portal'
import styles from './HoverCard.module.css'

export interface HoverCardProps {
  /**
   * What the card shows. Unlike a tooltip it can hold links and buttons,
   * since the pointer can move onto it.
   */
  content: ReactNode
  /**
   * The element that shows the card when hovered or focused.
   */
  children: ReactNode
  /**
   * Keeps the card closed, such as while the trigger's own menu is open.
   */
  isDisabled?: boolean
  placement?: Placement
}

/**
 * A card that shows more about an element after it's hovered for a moment,
 * or while it has keyboard focus.
 */
export function HoverCard({
  content,
  children,
  isDisabled,
  placement = 'bottom-start',
}: HoverCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  // Closes for good while disabled, so the card doesn't come back on its own
  // once whatever disabled it goes away.
  if (isDisabled && isOpen) {
    setIsOpen(false)
  }
  const [reference, setReference] = useState<HTMLElement | null>(null)
  const [floating, setFloating] = useState<HTMLElement | null>(null)
  const { floatingStyles, context } = useFloating({
    elements: { reference, floating },
    open: isOpen && !isDisabled,
    onOpenChange: setIsOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: !isDisabled,
      delay: { open: 500, close: 120 },
      handleClose: safePolygon(),
    }),
    useFocus(context, { enabled: !isDisabled, visibleOnly: true }),
    useDismiss(context),
  ])

  return (
    <>
      <span
        ref={setReference}
        className={styles.trigger}
        {...getReferenceProps()}
      >
        {children}
      </span>
      {isOpen && !isDisabled && (
        <Portal>
          <div
            ref={setFloating}
            className={styles.card}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {content}
          </div>
        </Portal>
      )}
    </>
  )
}
